/**
 * User Slice - RTK slice for user management
 * Converted from Redux-Thunk to RTK createAsyncThunk
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import toastr from 'toastr';
import { API_URL, getAuthHeaders } from '../../actions/utilAction';
import * as constant from '../../actions/constant';

// TypeScript interfaces
interface UserData {
  _id: string;
  name: string;
  email: string;
  username?: string;
  phone?: string;
  profilePic?: string;
  profilePhoto?: string;
  status?: string;
  isVerified?: boolean;
  isActive?: boolean;
  isAdsEnabled?: boolean;
  isTopSportUser?: boolean;
  country?: string;
  role?: { rolename: string };
  createdAt?: string;
  updatedAt?: string;
}

interface UserDetails {
  _id: string;
  name: string;
  email: string;
  username?: string;
  phone?: string;
  profilePic?: string;
  profilePhoto?: string;
  joinedDate?: string;
  country?: string;
  isActive?: boolean;
  isVerified?: boolean;
}

interface CsvDataRow {
  [key: string]: string | number | boolean | null;
}

interface UserState {
  userData: UserData[];
  userDetails: UserDetails | null;
  csvData: CsvDataRow[];
  totalCount: number;
  loading: boolean;
  error: string | null;
}

interface GetUsersParams {
  page: number;
  limit?: number;
  search?: string;
  search_string?: string;
  sortValue?: string;
  sortOrder?: number | string;
}

interface DeleteUserParams {
  userId: string;
}

interface UpdateProfileParams {
  userId: string;
  name?: string;
  email?: string;
  phone?: string;
}

interface SendMessageParams {
  userId?: string;
  userIds?: string[];
  message: string;
  title?: string;
}

interface MapUserParams {
  page?: number;
  sortValue?: string;
  sortOrder?: number | string;
  emails?: string[];
}

interface DisableUserParams {
  page?: number;
}

interface DeActivateUserParams {
  userId: string;
  page?: number;
  sortValue?: string;
  sortOrder?: number | string;
}

interface SettingsParams {
  isTopSportEnabled?: boolean;
  isMaintenanceMode?: boolean;
}

interface SettingsData {
  _id?: string;
  isTopSportEnabled?: boolean;
  isMaintenanceMode?: boolean;
}

// Initial state
const initialState: UserState = {
  userData: [],
  userDetails: null,
  csvData: [],
  totalCount: 0,
  loading: false,
  error: null,
};

// Async thunks

// Get users list
export const getUsers = createAsyncThunk<
  { userData: UserData[]; totalCount: number },
  GetUsersParams,
  { rejectValue: string }
>('user/getUsers', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}${constant.USER_LIST}`, params, {
      headers: getAuthHeaders()
    });
    if (response.data.code === 200) {
      return {
        userData: response.data.data,
        totalCount: response.data.totalCount
      };
    }
    return rejectWithValue(response.data.message || 'Failed to get users');
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
  }
});

// Get all users
export const getAllUsers = createAsyncThunk<
  { userData: UserData[]; totalCount: number },
  GetUsersParams,
  { rejectValue: string }
>('user/getAllUsers', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}${constant.ALL_USER_LIST}`, params, {
      headers: getAuthHeaders()
    });
    if (response.data.code === 200) {
      return {
        userData: response.data.data,
        totalCount: response.data.totalCount
      };
    }
    return rejectWithValue(response.data.message || 'Failed to get all users');
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
  }
});

// Map user to TopSport
export const mapUserToTopSport = createAsyncThunk<
  { userData: UserData[]; totalCount: number },
  MapUserParams,
  { rejectValue: string }
>('user/mapUserToTopSport', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}${constant.MAP_USERS}`, params, {
      headers: getAuthHeaders()
    });
    if (response.data.code === 200) {
      toastr.success(response.data.message);
      return {
        userData: response.data.data,
        totalCount: response.data.totalCount
      };
    }
    return rejectWithValue(response.data.message || 'Failed to map user');
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
  }
});

// Disable user to TopSport
export const disableUserToTopSport = createAsyncThunk<
  { userData: UserData[]; totalCount: number },
  DisableUserParams,
  { rejectValue: string }
>('user/disableUserToTopSport', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}${constant.DISABLE_USER}`, params, {
      headers: getAuthHeaders()
    });
    if (response.data.code === 200) {
      toastr.success(response.data.message);
      return {
        userData: response.data.data,
        totalCount: response.data.totalCount
      };
    }
    toastr.warning(response.data.message);
    return rejectWithValue(response.data.message || 'Failed to disable user');
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    toastr.warning(axiosError.response?.data?.message || 'Error disabling user');
    return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
  }
});

// Delete user
export const deleteUser = createAsyncThunk<
  { userData: UserData[]; totalCount: number },
  DeleteUserParams,
  { rejectValue: string }
>('user/deleteUser', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}${constant.USER_DELETE}`, params, {
      headers: getAuthHeaders()
    });
    if (response.data.code === 200) {
      toastr.success(response.data.message);
      // Fetch updated user list
      const listResponse = await axios.post(`${API_URL}${constant.USER_LIST}`, { page: 0 }, {
        headers: getAuthHeaders()
      });
      if (listResponse.data.code === 200) {
        return {
          userData: listResponse.data.data,
          totalCount: listResponse.data.totalCount
        };
      }
    }
    return rejectWithValue(response.data.message || 'Failed to delete user');
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
  }
});

// Delete multiple users
export const deleteUsers = createAsyncThunk<
  { userData: UserData[]; totalCount: number },
  { userIds: string[] },
  { rejectValue: string }
>('user/deleteUsers', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}${constant.USERS_DELETE}`, params, {
      headers: getAuthHeaders()
    });
    if (response.data.code === 200) {
      toastr.success(response.data.message);
      // Fetch updated user list
      const listResponse = await axios.post(`${API_URL}${constant.ALL_USER_LIST}`, { page: 0 }, {
        headers: getAuthHeaders()
      });
      if (listResponse.data.code === 200) {
        return {
          userData: listResponse.data.data,
          totalCount: listResponse.data.totalCount
        };
      }
    }
    return rejectWithValue(response.data.message || 'Failed to delete users');
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
  }
});

// Verify user
export const verifyUser = createAsyncThunk<
  { userData: UserData[]; totalCount: number },
  { userId: string },
  { rejectValue: string }
>('user/verifyUser', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}${constant.USER_VERIFY}`, params, {
      headers: getAuthHeaders()
    });
    if (response.data.code === 200) {
      toastr.success(response.data.message);
      // Fetch updated user list
      const listResponse = await axios.post(`${API_URL}${constant.USER_LIST}`, { page: 0 }, {
        headers: getAuthHeaders()
      });
      if (listResponse.data.code === 200) {
        return {
          userData: listResponse.data.data,
          totalCount: listResponse.data.totalCount
        };
      }
    }
    return rejectWithValue(response.data.message || 'Failed to verify user');
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
  }
});

// Deactivate user
export const deActivateUser = createAsyncThunk<
  { userData: UserData[] },
  DeActivateUserParams,
  { rejectValue: string }
>('user/deActivateUser', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}${constant.DEACTIVATE_USER}`, params, {
      headers: getAuthHeaders()
    });
    if (response.data.code === 200) {
      toastr.success(response.data.message);
      // Fetch updated user list
      const listResponse = await axios.post(`${API_URL}${constant.USER_LIST}`, params, {
        headers: getAuthHeaders()
      });
      if (listResponse.data.code === 200) {
        return { userData: listResponse.data.data };
      }
    } else {
      toastr.error(response.data.message);
    }
    return rejectWithValue(response.data.message || 'Failed to deactivate user');
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
  }
});

// Get user details
export const getUserDetails = createAsyncThunk<
  { userDetails: UserDetails },
  { userId: string },
  { rejectValue: string }
>('user/getUserDetails', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}${constant.USER_DETAILS}`, params, {
      headers: getAuthHeaders()
    });
    if (response.data.code === 200) {
      return { userDetails: response.data.data };
    }
    return rejectWithValue(response.data.message || 'Failed to get user details');
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
  }
});

// Update profile
export const updateProfile = createAsyncThunk<
  { userDetails: UserDetails },
  UpdateProfileParams,
  { rejectValue: string }
>('user/updateProfile', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}${constant.USER_UPDATE}`, params, {
      headers: getAuthHeaders()
    });
    if (response.data.code === 200) {
      toastr.success(response.data.message);
      // Fetch updated user details
      const detailsResponse = await axios.post(`${API_URL}${constant.USER_DETAILS}`, { userId: params.userId }, {
        headers: getAuthHeaders()
      });
      if (detailsResponse.data.code === 200) {
        return { userDetails: detailsResponse.data.data };
      }
    }
    return rejectWithValue(response.data.message || 'Failed to update profile');
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
  }
});

// Update profile pic
export const updateProfilePic = createAsyncThunk<
  { userDetails: UserDetails },
  { formData: FormData; userId: string },
  { rejectValue: string }
>('user/updateProfilePic', async ({ formData, userId }, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}${constant.PROFILE_PIC_UPDATE}`, formData, {
      headers: getAuthHeaders()
    });
    if (response.data.code === 200) {
      toastr.success(response.data.message);
      // Fetch updated user details
      const detailsResponse = await axios.post(`${API_URL}${constant.USER_DETAILS}`, { userId }, {
        headers: getAuthHeaders()
      });
      if (detailsResponse.data.code === 200) {
        return { userDetails: detailsResponse.data.data };
      }
    }
    return rejectWithValue(response.data.message || 'Failed to update profile pic');
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
  }
});

// Download CSV
export const downloadCsv = createAsyncThunk<
  { csvData: CsvDataRow[] },
  void,
  { rejectValue: string }
>('user/downloadCsv', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}/user/downloadCsv`, {}, {
      headers: getAuthHeaders()
    });
    if (response.data.code === 200) {
      return { csvData: response.data.data };
    }
    return rejectWithValue(response.data.message || 'Failed to download CSV');
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
  }
});

// Add user
export const addUser = createAsyncThunk<
  { userData: UserData[]; totalCount: number },
  FormData,
  { rejectValue: string }
>('user/addUser', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}${constant.ADD_USER}`, params, {
      headers: getAuthHeaders()
    });
    if (response.data.code === 200) {
      toastr.success(response.data.message);
      // Fetch updated user list
      const listResponse = await axios.post(`${API_URL}${constant.USER_LIST}`, { page: 0 }, {
        headers: getAuthHeaders()
      });
      if (listResponse.data.code === 200) {
        return {
          userData: listResponse.data.data,
          totalCount: listResponse.data.totalCount
        };
      }
    } else if (response.data.code === 201) {
      toastr.warning(response.data.message);
    }
    return rejectWithValue(response.data.message || 'Failed to add user');
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
  }
});

// Send message
export const sendMessage = createAsyncThunk<
  { userData: UserData[]; totalCount: number },
  SendMessageParams,
  { rejectValue: string }
>('user/sendMessage', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}${constant.SEND_MESSAGE}`, params, {
      headers: getAuthHeaders()
    });
    if (response.data.code === 200) {
      toastr.success(response.data.message);
      // Fetch updated user list
      const listResponse = await axios.post(`${API_URL}${constant.ALL_USER_LIST}`, { page: 0 }, {
        headers: getAuthHeaders()
      });
      if (listResponse.data.code === 200) {
        return {
          userData: listResponse.data.data,
          totalCount: listResponse.data.totalCount
        };
      }
    } else if (response.data.code === 201) {
      toastr.warning(response.data.message);
    }
    return rejectWithValue(response.data.message || 'Failed to send message');
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
  }
});

// Enable/disable ads
export const isEnabledAds = createAsyncThunk<
  void,
  SettingsParams,
  { rejectValue: string }
>('user/isEnabledAds', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}${constant.IS_ADS_ENABLED}`, params, {
      headers: getAuthHeaders()
    });
    if (response.data.code === 200) {
      toastr.success(response.data.message);
      return;
    }
    toastr.error(response.data.message);
    return rejectWithValue(response.data.message || 'Failed to update ads setting');
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
  }
});

// Get settings
export const getSettings = createAsyncThunk<
  SettingsData[],
  void,
  { rejectValue: string }
>('user/getSettings', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get(`${API_URL}${constant.IS_ADS_ENABLED}`, {
      headers: getAuthHeaders()
    });
    if (response.data.code === 200) {
      return response.data.data;
    }
    return rejectWithValue(response.data.message || 'Failed to get settings');
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
  }
});

// Maintenance mode
export const isMaintenanceMode = createAsyncThunk<
  void,
  SettingsParams,
  { rejectValue: string }
>('user/isMaintenanceMode', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_URL}${constant.IS_MAINTENANCE_MODE}`, params, {
      headers: getAuthHeaders()
    });
    if (response.data.code === 200) {
      toastr.success(response.data.message);
      return;
    }
    toastr.error(response.data.message);
    return rejectWithValue(response.data.message || 'Failed to update maintenance mode');
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string }>;
    return rejectWithValue(axiosError.response?.data?.message || axiosError.message);
  }
});

// User slice
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearUserDetails: (state) => {
      state.userDetails = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get users
      .addCase(getUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.userData = action.payload.userData;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      })
      // Get all users
      .addCase(getAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.userData = action.payload.userData;
        state.totalCount = action.payload.totalCount;
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      })
      // Map user to TopSport
      .addCase(mapUserToTopSport.fulfilled, (state, action) => {
        state.userData = action.payload.userData;
        state.totalCount = action.payload.totalCount;
      })
      // Disable user to TopSport
      .addCase(disableUserToTopSport.fulfilled, (state, action) => {
        state.userData = action.payload.userData;
        state.totalCount = action.payload.totalCount;
      })
      // Delete user
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.userData = action.payload.userData;
        state.totalCount = action.payload.totalCount;
      })
      // Delete users
      .addCase(deleteUsers.fulfilled, (state, action) => {
        state.userData = action.payload.userData;
        state.totalCount = action.payload.totalCount;
      })
      // Verify user
      .addCase(verifyUser.fulfilled, (state, action) => {
        state.userData = action.payload.userData;
        state.totalCount = action.payload.totalCount;
      })
      // Deactivate user
      .addCase(deActivateUser.fulfilled, (state, action) => {
        state.userData = action.payload.userData;
      })
      // Get user details
      .addCase(getUserDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.userDetails = action.payload.userDetails;
      })
      .addCase(getUserDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      })
      // Update profile
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.userDetails = action.payload.userDetails;
      })
      // Update profile pic
      .addCase(updateProfilePic.fulfilled, (state, action) => {
        state.userDetails = action.payload.userDetails;
      })
      // Download CSV
      .addCase(downloadCsv.fulfilled, (state, action) => {
        state.csvData = action.payload.csvData;
      })
      // Add user
      .addCase(addUser.fulfilled, (state, action) => {
        state.userData = action.payload.userData;
        state.totalCount = action.payload.totalCount;
      })
      // Send message
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.userData = action.payload.userData;
        state.totalCount = action.payload.totalCount;
      });
  },
});

export const { clearUserDetails, clearError } = userSlice.actions;
export default userSlice.reducer;
